import type { Identifier } from "ra-core";

import type {
  DealActivityType,
  DealStageActivity,
  StagePlacement,
} from "../types";

/**
 * Almikia Epic EP (TASK-105): pure, framework-free helpers backing the
 * "Etapas" admin screen -- key derivation, hours/days conversion, label and
 * activities validation, next-position and reorder-swap computation. Kept
 * free of react-admin/react coupling so they are unit-testable on their own
 * (see `stageConfigForm.test.ts`), mirroring the shape of
 * `incentives/incentiveProgramForm.ts`.
 */

const STAGE_KEY_MAX_LENGTH = 40;

/** Strips combining diacritical marks (accents) after NFD normalization. */
function stripAccents(value: string): string {
  return value.normalize("NFD").replace(/[̀-ͯ]/g, "");
}

/**
 * The unsuffixed, lowercase ASCII snake_case slug for a stage label, per the
 * `deal_stage_configs.key` format (`^[a-z][a-z0-9_]{1,39}$`, DATA CONTRACT EP
 * (1)). Never used directly for a new stage without going through
 * {@link deriveStageKey} first, which also guarantees uniqueness.
 */
export function slugifyStageKeyBase(label: string): string {
  const ascii = stripAccents(label).toLowerCase();
  const snake = ascii.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  // The format requires at least 2 chars total and a leading letter; a label
  // that strips down to nothing usable (e.g. only punctuation) needs an
  // explicit fallback rather than the bare "e_" prefix below.
  if (snake === "") {
    return "e_etapa";
  }
  const withLeadingLetter = /^[a-z]/.test(snake) ? snake : `e_${snake}`;
  return withLeadingLetter.slice(0, STAGE_KEY_MAX_LENGTH);
}

/**
 * Derives a `deal_stage_configs.key` from a new stage's label: the slug from
 * {@link slugifyStageKeyBase}, suffixed with `_2`, `_3`, … when already taken
 * by `existingKeys` -- the key is never editable afterwards (ticket
 * description), so this is the only place it is ever computed.
 */
export function deriveStageKey(
  label: string,
  existingKeys: readonly string[],
): string {
  const base = slugifyStageKeyBase(label);
  const taken = new Set(existingKeys);
  if (!taken.has(base)) return base;

  let suffix = 2;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const suffixText = `_${suffix}`;
    const maxBaseLength = STAGE_KEY_MAX_LENGTH - suffixText.length;
    const candidate = `${base.slice(0, maxBaseLength)}${suffixText}`;
    if (!taken.has(candidate)) return candidate;
    suffix += 1;
  }
}

export type TargetTimeUnit = "hours" | "days";

/** The list's "target time" column: `{ unit: 'none' }` renders "Sin tiempo objetivo". */
export interface TargetTimeDisplay {
  unit: "none" | TargetTimeUnit;
  value: number | null;
}

/**
 * DATA CONTRACT EP (1): a stage's `target_hours` shown as hours when at most
 * 24, or when it is not a whole number of days, otherwise as days (144 -> 6
 * días). This keeps the seeded examples exact: 24 stays "24 h", 144 becomes
 * "6 días".
 */
export function resolveTargetTimeDisplay(
  targetHours: number | null | undefined,
): TargetTimeDisplay {
  if (targetHours == null) return { unit: "none", value: null };
  if (targetHours <= 24 || targetHours % 24 !== 0) {
    return { unit: "hours", value: targetHours };
  }
  return { unit: "days", value: targetHours / 24 };
}

/** Prefills the edit form's unit/value pair from a stored `target_hours`. */
export function targetTimeInputFromHours(
  targetHours: number | null | undefined,
): { unit: TargetTimeUnit; value: number | null } {
  const display = resolveTargetTimeDisplay(targetHours);
  if (display.unit === "none") return { unit: "hours", value: null };
  return { unit: display.unit, value: display.value };
}

/**
 * Converts the edit form's unit + value pair back to `target_hours` for
 * storage: an empty value stores `NULL`, "6 días" stores 144.
 */
export function targetHoursFromInput(
  unit: TargetTimeUnit,
  value: number | string | null | undefined,
): number | null {
  if (value == null || value === "") return null;
  const num = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(num)) return null;
  return unit === "days" ? num * 24 : num;
}

/** react-hook-form validator for the target time value field (hours or days). */
export function validateTargetTimeValue(
  value: number | string | null | undefined,
): string | undefined {
  if (value == null || value === "") return undefined;
  const num = typeof value === "string" ? Number(value) : value;
  if (!Number.isInteger(num) || num <= 0) {
    return "resources.deal_stage_configs.error.target_time_invalid";
  }
  return undefined;
}

export const STAGE_LABEL_MAX_LENGTH = 40;

/**
 * DATA CONTRACT EP (1): a stage `label`, trimmed non-blank, at most 40
 * chars, unique case-insensitively among `activeLabels` (every OTHER
 * non-archived stage's label -- the caller excludes the record's own current
 * label before calling this on an edit).
 */
export function validateStageLabel(
  label: string | null | undefined,
  activeLabels: readonly string[],
): string | undefined {
  const trimmed = (label ?? "").trim();
  if (trimmed === "") {
    return "resources.deal_stage_configs.error.label_required";
  }
  if (trimmed.length > STAGE_LABEL_MAX_LENGTH) {
    return "resources.deal_stage_configs.error.label_too_long";
  }
  const normalized = trimmed.toLowerCase();
  const duplicate = activeLabels.some(
    (existing) => existing.trim().toLowerCase() === normalized,
  );
  if (duplicate) {
    return "resources.deal_stage_configs.error.label_duplicate";
  }
  return undefined;
}

export const MAX_REQUIRED_ACTIVITIES = 10;
export const REQUIRED_ACTIVITY_MAX_LENGTH = 80;

/**
 * DATA CONTRACT EP (1): `required_activities`, a PLACEHOLDER list ("Actividades
 * de la etapa") of at most 10 free-text items, each trimmed non-blank and at
 * most 80 chars.
 */
export function validateRequiredActivities(
  items: readonly (string | null | undefined)[] | null | undefined,
): string | undefined {
  if (!items || items.length === 0) return undefined;
  if (items.length > MAX_REQUIRED_ACTIVITIES) {
    return "resources.deal_stage_configs.error.activities_too_many";
  }
  const invalid = items.some(
    (item) =>
      item == null ||
      item.trim() === "" ||
      item.trim().length > REQUIRED_ACTIVITY_MAX_LENGTH,
  );
  if (invalid) {
    return "resources.deal_stage_configs.error.activities_invalid";
  }
  return undefined;
}

/** "Nueva etapa": only these two placements are ever offered (never 'inbox'). */
export const NEW_STAGE_PLACEMENT_CHOICES: Array<{
  id: Extract<StagePlacement, "kanban" | "special">;
  name: string;
}> = [
  { id: "kanban", name: "resources.deal_stage_configs.group.kanban" },
  { id: "special", name: "resources.deal_stage_configs.group.special" },
];

/**
 * "Nueva etapa" is appended LAST in its placement group (ticket description):
 * one position step past the highest `position` already used in that group
 * (archived rows included, so a freed slot is never reused).
 */
export function nextStagePosition(
  configs: readonly { placement: string; position: number }[],
  placement: string,
): number {
  const positions = configs
    .filter((config) => config.placement === placement)
    .map((config) => config.position);
  return positions.length > 0 ? Math.max(...positions) + 10 : 10;
}

/**
 * DATA CONTRACT EP (10): maps a save/write error to a Spanish message, so a
 * raw Postgres/RLS error text is never shown to direccion. A duplicate-label
 * write refused by Postgres's `uq__deal_stage_configs__label_active` unique
 * index (code `23505`, or the equivalent text) maps to the same
 * `label_duplicate` message as the FakeRest guard; an RLS refusal (code
 * `42501`, or "row-level security" in the text) maps to `not_authorized`. A
 * FakeRest guard error is already a plain Spanish message (see
 * `dealStageConfigsProvider.ts`), so it passes through UNCHANGED here --
 * only a message-less error falls back to the generic `generic` message.
 */
export function toStageConfigErrorMessage(error: unknown): string {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String((error as { code?: unknown }).code)
      : undefined;
  const message = error instanceof Error ? error.message : undefined;

  if (code === "23505" || /duplicate key/i.test(message ?? "")) {
    return "resources.deal_stage_configs.error.label_duplicate";
  }
  if (code === "42501" || /row-level security/i.test(message ?? "")) {
    return "resources.deal_stage_configs.error.not_authorized";
  }
  return message || "resources.deal_stage_configs.error.generic";
}

/**
 * Almikia Epic EA (TASK-115): the diff between a stage's picked catalogue
 * activities (`selectedActivityTypeIds`) and its existing `deal_stage_activities`
 * rows (any `enabled` state -- an already-disabled link is only re-enabled,
 * never re-created, DATA CONTRACT EA (3)) -- three disjoint write sets so the
 * save step writes ONLY the rows that actually change (the ticket's own
 * performance requirement). A row already `enabled` and still selected, or
 * already disabled and still unselected, is untouched.
 */
export interface StageActivityLinkDiff {
  /** Activity ids with no existing link row at all -- a new one is created. */
  toCreate: Identifier[];
  /** Existing disabled link rows to re-enable. */
  toEnable: Array<{ id: Identifier }>;
  /** Existing enabled link rows to disable. */
  toDisable: Array<{ id: Identifier }>;
}

export function computeStageActivityLinkDiff(
  selectedActivityTypeIds: ReadonlySet<Identifier>,
  existingLinks: readonly Pick<
    DealStageActivity,
    "id" | "activity_type_id" | "enabled"
  >[],
): StageActivityLinkDiff {
  const linkByActivityId = new Map(
    existingLinks.map((link) => [link.activity_type_id, link]),
  );

  const toCreate: Identifier[] = [];
  const toEnable: Array<{ id: Identifier }> = [];
  for (const activityTypeId of selectedActivityTypeIds) {
    const existing = linkByActivityId.get(activityTypeId);
    if (!existing) {
      toCreate.push(activityTypeId);
    } else if (!existing.enabled) {
      toEnable.push({ id: existing.id });
    }
  }

  const toDisable = existingLinks
    .filter(
      (link) =>
        link.enabled && !selectedActivityTypeIds.has(link.activity_type_id),
    )
    .map((link) => ({ id: link.id }));

  return { toCreate, toEnable, toDisable };
}

export interface StageActivitySummaryItem {
  name: string;
  archived: boolean;
}

/**
 * Almikia Epic EA (TASK-115): a stage's ENABLED-linked catalogue activities,
 * for display next to its EP free-text `required_activities` list -- ordered
 * `(position, id)` (DATA CONTRACT EA (10)'s tie-break), archived ones
 * included (unlike the log form, the Etapas list must still show them,
 * marked 'Archivada').
 */
export function resolveStageActivitySummary(
  stageKey: string,
  activityTypes: readonly Pick<
    DealActivityType,
    "id" | "name" | "position" | "archived_at"
  >[],
  stageActivities: readonly Pick<
    DealStageActivity,
    "stage_key" | "activity_type_id" | "enabled"
  >[],
): StageActivitySummaryItem[] {
  const linkedIds = new Set(
    stageActivities
      .filter((link) => link.enabled && link.stage_key === stageKey)
      .map((link) => link.activity_type_id),
  );

  return activityTypes
    .filter((activity) => linkedIds.has(activity.id))
    .slice()
    .sort((a, b) => {
      if (a.position !== b.position) return a.position - b.position;
      return String(a.id).localeCompare(String(b.id), undefined, {
        numeric: true,
      });
    })
    .map((activity) => ({
      name: activity.name,
      archived: activity.archived_at != null,
    }));
}

/** One `deal_stage_configs` row's position update, computed by a reorder swap. */
export interface StagePositionUpdate {
  id: Identifier;
  position: number;
}

/**
 * "Subir" / "Bajar" (ticket description): swaps the `position` of the stage
 * at `currentValue` with its previous/next sibling in the SAME placement
 * group (`group`, already sorted in display order) -- at most two row
 * updates, matching the ticket's own performance requirement. Returns
 * `undefined` when there is no sibling to swap with (already first/last) or
 * either row's id can't be resolved.
 */
export function computeStageReorderSwap(
  group: readonly { value: string; position: number }[],
  currentValue: string,
  direction: "up" | "down",
  idByKey: (value: string) => Identifier | undefined,
): [StagePositionUpdate, StagePositionUpdate] | undefined {
  const index = group.findIndex((stage) => stage.value === currentValue);
  if (index < 0) return undefined;
  const otherIndex = direction === "up" ? index - 1 : index + 1;
  if (otherIndex < 0 || otherIndex >= group.length) return undefined;

  const current = group[index];
  const other = group[otherIndex];
  const currentId = idByKey(current.value);
  const otherId = idByKey(other.value);
  if (currentId == null || otherId == null) return undefined;

  return [
    { id: currentId, position: other.position },
    { id: otherId, position: current.position },
  ];
}
